/**
 * @fileoverview Navigation bridge for code outside the React tree.
 * @description React Router's own docs recommend this exact pattern for navigating outside a
 * component: `createBrowserRouter` returns a plain router object, not something tied to React, so
 * it can be kept as a module-level reference and called directly (see "Navigating outside of
 * Components" in the React Router docs). Here that reference is stored in `shared` rather than
 * imported straight from `app/routing/AppRoutes` because `shared` can't import `app` per
 * ADR-0002's layering rule — AppRoutes registers it here once, right after creating the router,
 * and the Axios response interceptor (which has no router context of its own) reads it back to
 * redirect on a 403/5xx response.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

let routerNavigate: ((to: string) => unknown) | null = null

/**
 * @description Registers the router's navigate function for use outside React. Called once by
 * AppRoutes right after `createBrowserRouter` runs.
 * @param {(to: string) => unknown} navigate - The router's imperative navigate function.
 */
export function setRouterNavigate(navigate: (to: string) => unknown): void {
  routerNavigate = navigate
}

/**
 * @description Navigates to a route from outside the React tree. A no-op if called before the
 * router has registered itself, or when already on that route.
 * @param {string} to - The path to navigate to.
 */
export function navigateOutsideReact(to: string): void {
  if (!routerNavigate || window.location.pathname === to) {
    return
  }

  void routerNavigate(to)
}
